import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";

import TemplateCreateRTEditor from '@/views/pages/Template/elements/TemplateCreateRTEditor';

import { DropdownIndicator, NoOptionsMessage } from '@/views/components/CustomSelect/CustomSelect';

import { Button, Checkbox, TextField, FormControlLabel, Typography } from '@mui/material';

import { ModalContext } from '@/utils/contexts/ModalContext';
import { CanHandleDepartments } from '@/utils/permissions/permissions';

import Alert from '@/views/components/Alert/Alert';

import styles from '@/views/pages/Template/Template.module.scss';
import AddLetterDeptAutoSuggest from "@/views/pages/AddLetter/elements/AddLetterDeptAutoSuggest";

const colourStyles = {
	control: styles => ({ ...styles, backgroundColor: '#FCFCFC', borderColor: '#DADADA', maxHeight: '32px', height: '32px', minHeight: '0' }),
	valueContainer: styles => ({ ...styles, maxHeight: '32px', height: '32px' }),
	indicatorsContainer: styles => ({ ...styles, maxHeight: '32px', height: '32px' }),
	singleValue: styles => ({ ...styles, fontSize: "0.75rem"}),
};

const TemplateCreateForm = ({
	formInitialValues,
	metricItems,
	selectCategories,
	editMode,
	uploadTemplateData,
	templateData,
}) => {
	const navigate = useNavigate();
	const { id: urlId } = useParams();
	const { dict, mode } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);
	const { control, handleSubmit, setValue, watch } = useForm({ defaultValues: formInitialValues });

	const { showModal } = useContext(ModalContext);

	const [ submitDisabled, setSubmitDisabled ] = useState(true);
	const [ headerAndFooterSameAsLetter, setHeaderAndFooterSameAsLetter ] = useState(true);

	const watchCategory = watch('category');
	const watchTemplateName = watch('templateName');
	const watchDepartmentId = watch('departmentId');

	useEffect(() => {
		if (!formInitialValues.category) {
			setValue('category', selectCategories[0]);
		} else {
			const index = selectCategories.findIndex(item => item.value === formInitialValues.category);
			if (index < 0) {
				setValue('category', selectCategories[0]);
			} else {
				setValue('category', selectCategories[index]);
			}
		}

		if(editMode) {
			if(formInitialValues.departmentId !== formInitialValues.headerId || formInitialValues.departmentId !== formInitialValues.footerId) {
				setHeaderAndFooterSameAsLetter(false);
			}
		}

	}, [ formInitialValues ]);

	useEffect(() => {
		if (watchCategory && watchTemplateName && watchTemplateName.length > 0 && watchDepartmentId) return setSubmitDisabled(false);
		setSubmitDisabled(true);
	}, [ watchCategory, watchTemplateName, watchDepartmentId ]);

	const parseMetrics = React.useCallback((dataObject) => {
		return Object.keys(dataObject).filter(item => item.split('-')[0] === 'metric' && dataObject[item] === true).map(mappedItem => ({
			type: {
				code: mappedItem,
			},
		}));
	}, []);

	const onSubmit = async (data) => {
		const apiData = {
			id: 0,
			title: data.templateName,
			category: data.category.value,
			baseForCategory: data.baseForCategory,
			letter: {
				id: urlId,
			},
			department: {
				id: data.departmentId,
			},
			footer: {
				id: headerAndFooterSameAsLetter ? data.departmentId : data.footerId,
			},
			header: {
				id: headerAndFooterSameAsLetter ? data.departmentId : data.headerId,
			},
			metricFields: parseMetrics(data),
			contentFields: [
				{
					content: data.letterTitle,
					type: {
						code: "content-letter-title",
					},
				},
				{
					content: data.letterContent,
					type: {
						code: "content-letter-text",
					},
				},
				{
					content: data.regulations,
					type: {
						code: "content-letter-rights",
					},
				},
			],
			version: data.noUpdatesNeeded ? 'final' : null,
		};
		uploadTemplateData(apiData);
	};

	return (
		<>
			<Alert
				style={ { marginBottom: 0 } }
				variant='info'
				alertContent={ <><strong>{ dict.templates.create.warn1 }</strong> { dict.templates.create.warn2 }</> }
			/>
			{ templateData && templateData.version === 'update-required' && <Alert
				style={ { marginBottom: 0 } }
				variant='warning'
				alertContent={ <><strong>{ dict.templates.create.warnUpdated.part1 }</strong>{ dict.templates.create.warnUpdated.part2 }</> }
			/> }
			<div className={ styles['form'] }>
				<form onSubmit={ handleSubmit(onSubmit) }>
					<section className={ styles['form__section'] }>
						<div className={ `${styles['form__section__title']}` }>
							{dict.templates.create.generalInformations}
						</div>

						<div className={ styles['form__section__comment'] }>
							{dict.templates.create.generalInformationsDescription}
						</div>

						<div className={ styles['form__section__needed-field'] }>
							{dict.templates.create.requiredFields}
						</div>

						<div
							className={ styles['form__titletype'] }
						>
							<div>
								<label className={ `${styles['form__label']}` }>{dict.templates.create.templateName}</label>
								<Controller
									render={ ({ field }) => <TextField
										size='small'
										fullWidth
										inputProps={ { maxLength: 4096 } }
										{ ...field }
									/> }
									name='templateName'
									control={ control }
									// helperText={ errors[item] && errors[item].message }
								/>
							</div>
							<div>
								<label className={ `${styles['form__label']}` }>{dict.templates.create.category}</label>
								<Controller
									name='category'
									render={ ({ field }) => <Select
										options={ selectCategories }
										styles={ colourStyles }
										isSearchable={ false }
										className={ styles }
										classNamePrefix='letters__searchbar--filters-select'
										components={ {
											DropdownIndicator,
											NoOptionsMessage,
											IndicatorSeparator: () => null,
										} }
										{ ...field }
									/> }
									control={ control }
									// rules={{ required: true }}
								/>
							</div>
							<Controller
								name='departmentId'
								control={ control }
								render={ ({ field }) => {
									return (
										<AddLetterDeptAutoSuggest
											label={ dict.addLetter.department }
											setValueExt={ (id, value) => {
												setValue("departmentId", value);
											} }
											defaultValue={ (!editMode && user?.departments?.length > 0) ?
												{
													id: user?.departments[0]?.id,
													label: user?.departments[0]?.name,
												} : field.value
											}
											disabled={ !CanHandleDepartments(user.role, mode) }
										/>
									);
								} }
							/>
							{CanHandleDepartments(user.role, mode) &&
								<div className={ styles['form__grid__item'] }>
									<FormControlLabel
										control={
											<Checkbox
												color='primary'
												onChange={ () => setHeaderAndFooterSameAsLetter(state => !state) }
												checked={ headerAndFooterSameAsLetter }
											/>
										}
										label={ <Typography className={ styles['form__checkbox__label'] }>{dict.templates.create.headerAndFooterSameAsLetter}</Typography> }
									/>
								</div>
							}
							{
								!headerAndFooterSameAsLetter && (
									<>
										<Controller
											name='headerId'
											control={ control }
											render={ ({ field }) => {
												return (
													<AddLetterDeptAutoSuggest
														label={ dict.templates.create.header }
														setValueExt={ (id, value) => {
															setValue("headerId", value);
														} }
														defaultValue={ field.value }
													/>
												);
											} }
										/>
										<Controller
											name='footerId'
											control={ control }
											render={ ({ field }) => (
												<AddLetterDeptAutoSuggest
													label={ dict.templates.create.footer }
													setValueExt={ (id, value) => {
														setValue('footerId', value);
													} }
													defaultValue={ field.value }
												/>
											) }
										/>
									</>
								)
							}
						</div>
					</section>

					{/* Metryczka */}
					<section className={ styles['form__section'] }>
						<div className={ `${styles['form__section__title']}` }>
							{dict.templates.create.metricHeader}
						</div>
						<div className={ styles['form__section__comment'] }>
							{dict.templates.create.metricDescription}
						</div>
						<div className={ `${styles['form__grid']}` }>
							{metricItems && metricItems.length && metricItems.map(({ label, code }) => {
								return (
									<div
										key={ code }
										className={ styles['form__grid__item'] }
									>
										<FormControlLabel
											control={
												<Controller
													name={ code }
													control={ control }
													render={ ({ field }) => {
														return (
															<Checkbox
																color='primary'
																onChange={ (e) => field.onChange(e.target.checked) }
																checked={ field.value }
															/>
														);
													} }
												/>
											}
											label={ <Typography className={ styles['form__checkbox__label'] }>{label}</Typography> }
										/>
									</div>
								);
							})}
						</div>
					</section>

					{/* Letter content */}
					<section className={ styles['form__section'] }>
						<div className={ `${styles['form__section__title']}` }>
							{dict.templates.create.letterContentTitle}
						</div>
						<div>
							<label className={ `${styles['form__label']}` }>{dict.templates.create.letterTitleLabel}</label>
							<Controller
								render={ ({ field }) => <TextField
									size='small'
									fullWidth
									{ ...field }
								/> }
								name='letterTitle'
								control={ control }
								// helperText={ errors[item] && errors[item].message }
							/>
						</div>
						<div>
							<label className={ `${styles['form__label']}` }>{dict.templates.create.letterContentLabel}</label>
							<div className={ styles['form__section__comment'] }>
								{ dict.templates.create.letterContentLabelDescription }
								<span 
									className={ styles['form__section__comment--link'] }
									onClick={ () => {
										showModal({
											componentName: 'ModalHowToCreateSimpleTexts',
										});
									} }
								>
									{ dict.templates.create.letterContentLabelDescriptionLink }
								</span>
							</div>
							<TemplateCreateRTEditor
								name='letterContent'
								control={ control }
								cutted={ false }
								defaultValue={ formInitialValues.letterContent }
								// defaultValue={description || null}
								// required={true}
								// placeholder='Write a Description...'
							/>
						</div>
						<div>
							<label className={ `${styles['form__label']}` }>{dict.templates.create.regulationsLabel}</label>
							<div className={ styles['form__section__comment'] }>
								{dict.templates.create.regulationsLabelDescription}
							</div>
							<TemplateCreateRTEditor
								name='regulations'
								control={ control }
								cutted={ true }
							/>
						</div>
					</section>
					
					{editMode && templateData && templateData.letter.version === 'final' && templateData.version === 'update-required' && (
						<div style={ { marginTop: "20px" } }>
							<FormControlLabel
								control={
									<Controller
										name={ 'noUpdatesNeeded' }
										control={ control }
										render={ ({ field }) => {
											return (
												<Checkbox
													color='primary'
													onChange={ (e) => field.onChange(e.target.checked) }
													checked={ field.value }
												/>
											);
										} }
									/>
								}
								label={ <Typography className={ styles['form__checkbox__label'] }>{ dict.templates.create.updateNotNeeded }</Typography> }
							/>
						</div>
					)}

					<section className={ styles['form__buttons__section'] }>
						<div className={ styles['form__buttons__content'] }>
							<Button
								color='primary'
								variant='contained'
								className='button__transparent'
								sx={ { textTransform: 'none' } }
								onClick={ () => navigate(-1) }
							>
								{dict.cancel}
							</Button>
							<Button
								color='primary'
								type='submit'
								variant='contained'
								fullWidth
								className='button__blue'
								sx={ { textTransform: 'none' } }
								disabled={ submitDisabled }
							>
								{editMode ? dict.templates.create.editTemplateSubmitButton : dict.templates.create.createTemplate}
							</Button>
						</div>
					</section>
				</form>
			</div>
		</>
	);
};

export default TemplateCreateForm;
